import { useChangePassword, useUpdateUserInfo, useUserInfo } from "@/api/user";
import DesktopAppBanner from "@/components/AdminPanel/Account/DesktopAppBanner";
import {
  DirtyField,
  LcBadge,
  NESTED_DIALOG_Z,
  PaneBody,
  PaneSection,
  SelectSm,
  SettingRow,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SelectItem } from "@/components/ui/select";
import * as analytics from "@/utils/analytics";
import { PersonAvatar } from "@cohesive/ui-components";
import React, { useState } from "react";
import toast from "react-hot-toast";

/**
 * Profile — the Manage modal's Settings·Profile scope. Who you are to
 * Cohesive: the identity masthead, the two fields the API actually stores,
 * how you sign in, and the exit.
 *
 * Free text saves on dirty (a quiet Save appears only while edited); the
 * timezone select applies instantly. Email is display-only — it comes from
 * the identity provider, and there is no change-email path.
 */

/**
 * The US zones, west to east, plus the two non-contiguous states. A curated
 * short list rather than the full IANA set: this product's audience is US-based,
 * and a hundred-entry picker is worse than no picker. Alaska and Hawaii are here
 * because leaving them out would make the field simply wrong for those users.
 */
const TIME_ZONES = [
  { id: "America/Los_Angeles", name: "Pacific Time" },
  { id: "America/Denver", name: "Mountain Time" },
  { id: "America/Chicago", name: "Central Time" },
  { id: "America/New_York", name: "Eastern Time" },
  { id: "America/Anchorage", name: "Alaska Time" },
  { id: "Pacific/Honolulu", name: "Hawaii-Aleutian Time" },
] as const;

/**
 * "Eastern Time (UTC−04:00)" — the offset is computed rather than written down,
 * so it stays right across daylight saving instead of being wrong half the year.
 */
function zoneLabel(zone: { id: string; name: string }): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone.id,
      timeZoneName: "longOffset",
    }).formatToParts(new Date());
    const offset = parts.find((part) => part.type === "timeZoneName")?.value;
    // Intl gives "GMT-4" or "GMT-04:00"; normalise to the UTC−04:00 shape.
    const match = offset?.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (!match) return zone.name;
    const [, sign, hours, minutes = "00"] = match;
    return `${zone.name} (UTC${sign === "-" ? "−" : "+"}${hours.padStart(2, "0")}:${minutes})`;
  } catch {
    return zone.name;
  }
}

const MySettings: React.FC = () => {
  const { data: userInfo } = useUserInfo();
  const updateUserInfo = useUpdateUserInfo();
  const changePassword = useChangePassword();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const user = userInfo?.user;
  const metadata = userInfo?.organization_user?.metadata;

  /** Every field writes the whole profile payload; only its own value changes. */
  const saveProfile = async (
    patch: Partial<{
      first_name: string;
      last_name: string;
      job_title: string;
      time_zone: string;
    }>,
  ) => {
    try {
      await updateUserInfo.mutateAsync({
        user: {
          first_name: patch.first_name ?? user?.first_name ?? "",
          last_name: patch.last_name ?? user?.last_name ?? "",
        },
        organization_user: {
          // Metadata is overwritten wholesale server-side — carry the keys
          // this pane doesn't show (they feed the agent) rather than clearing
          // them.
          metadata: {
            job_title: patch.job_title ?? metadata?.job_title ?? "",
            time_zone: patch.time_zone ?? metadata?.time_zone ?? "",
            department: metadata?.department ?? "",
            special_instructions: metadata?.special_instructions ?? "",
          },
        },
      });
      analytics.trackUserProfileEdited({
        editedFields: Object.keys(patch),
      });
      toast.success("Profile updated.");
    } catch (error) {
      toast.error("Failed to update your profile. Please try again.");
      // DirtyField keeps the typed value only if this rejects.
      throw error;
    }
  };

  return (
    <PaneBody>
      <div className="flex items-center gap-3 px-2.5">
        <PersonAvatar
          // Falls back to the email so an account with no name set still gets
          // a monogram rather than an empty disc.
          name={user?.name || user?.email || ""}
          email={user?.email ?? ""}
          pictureUrl={user?.picture_url}
          className="size-10 text-sm"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-md font-medium text-n-90">
            {user?.name || "—"}
          </div>
          <div className="truncate text-sm text-n-50">{user?.email}</div>
        </div>
      </div>

      <DesktopAppBanner />

      <div className="flex flex-col gap-4">
        <DirtyField
          label="First name"
          initial={user?.first_name ?? ""}
          isSaving={updateUserInfo.isPending}
          onSave={(value) => saveProfile({ first_name: value })}
        />
        <DirtyField
          label="Last name"
          initial={user?.last_name ?? ""}
          isSaving={updateUserInfo.isPending}
          onSave={(value) => saveProfile({ last_name: value })}
        />
        <DirtyField
          label="Job title"
          initial={metadata?.job_title ?? ""}
          isSaving={updateUserInfo.isPending}
          onSave={(value) => saveProfile({ job_title: value })}
        />

        <div className="px-2.5">
          <span className="text-sm font-medium text-n-70">Time zone</span>
          <div className="mt-1.5">
            <SelectSm
              value={metadata?.time_zone || undefined}
              onValueChange={(value) => {
                void saveProfile({ time_zone: value }).catch(() => {
                  // Already surfaced as a toast; nothing further to do.
                });
              }}
              ariaLabel="Time zone"
              placeholder="Select a time zone"
              size="field"
              className="w-56"
            >
              {TIME_ZONES.map((zone) => (
                <SelectItem key={zone.id} value={zone.id}>
                  {zoneLabel(zone)}
                </SelectItem>
              ))}
            </SelectSm>
          </div>
        </div>
      </div>

      <PaneSection head="Sign-in">
        <SettingRow label="Email" desc={user?.email}>
          <LcBadge>Verified</LcBadge>
        </SettingRow>
        {user?.provider === "auth0" && (
          <SettingRow
            label="Password"
            desc="We email you a link to set a new one"
          >
            <Button
              size="sm"
              variant="secondary"
              className={smButtonClassName}
              onClick={() => setPasswordDialogOpen(true)}
            >
              Change password
            </Button>
          </SettingRow>
        )}
      </PaneSection>

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent
          className={NESTED_DIALOG_Z}
          overlayClassName={NESTED_DIALOG_Z}
        >
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              We&apos;ll email you a link to set a new password.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setPasswordDialogOpen(false)}
            >
              Not now
            </Button>
            <Button
              disabled={changePassword.isPending}
              isLoading={changePassword.isPending}
              onClick={() => {
                changePassword
                  .mutateAsync()
                  .then(() => {
                    setPasswordDialogOpen(false);
                    toast.success("Check your email for the reset link.");
                  })
                  .catch((error: Error) => toast.error(error.message));
              }}
            >
              Send reset email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PaneBody>
  );
};

export default MySettings;
